// 261010 Epic 2 / P2a — logic THUẦN của khung mới "bảng 9 bước" (chỉ đọc). KHÔNG import antd / react-router (jest của CRA không nạp được — xem pipelineStatus.ts).
// Nhãn/thứ tự theo mẫu P1 v6 đã chốt (`project-docs/261010-hoc-luong-sky/_p1/mockup-chot`). Trạng thái do SERVER tính (BE `filmWorkflowStatus.ts`); file này chỉ
// lọc / nhóm / gắn nhãn / dựng đường dẫn — không tự suy trạng thái.
import type { FilmFailedJob, FilmRunningJob } from '../../types/film';
import type {
  WorkflowAlert, WorkflowAlertSeverity, WorkflowEventStatus, WorkflowEventStepKey, WorkflowStepKey, WorkflowStepState, WorkflowStepSummary,
} from '../../types/filmWorkflow';

export interface WorkflowStepMeta { key: WorkflowStepKey; no: number; name: string; scope: 'PLAN' | 'EVENT'; desc: string }
export const WORKFLOW_STEPS: WorkflowStepMeta[] = [
  { key: 'SCRIPT', no: 1, name: 'Phân đoạn', scope: 'PLAN', desc: 'Chia video theo tình huống: hành động, các giai đoạn, thoại.' },
  { key: 'ASSETS', no: 2, name: 'Tài sản', scope: 'PLAN', desc: 'Nhân vật, bối cảnh, đạo cụ — ảnh tham chiếu dùng cho cả plan.' },
  { key: 'DIRECTING', no: 3, name: 'Đạo diễn & diễn xuất', scope: 'EVENT', desc: 'Các shot và diễn xuất của từng video — phân cảnh · dàn shot.' },
  { key: 'CINEMATOGRAPHY', no: 4, name: 'Quay phim', scope: 'EVENT', desc: 'Hướng mặt chủ thể và nhịp truyện của từng shot; chỉnh cỡ, góc, máy ở đây.' },
  { key: 'MAP', no: 5, name: 'Sơ đồ định vị', scope: 'EVENT', desc: 'Sơ đồ định vị người trong bối cảnh theo từng giai đoạn.' },
  { key: 'STORYBOARD', no: 6, name: 'Storyboard', scope: 'EVENT', desc: 'Tấm storyboard của video.' },
  { key: 'PROMPT', no: 7, name: 'Prompt', scope: 'EVENT', desc: 'Prompt gửi engine tạo video.' },
  { key: 'VIDEO', no: 8, name: 'Video', scope: 'EVENT', desc: 'Các bản clip và bản được chọn.' },
  { key: 'ASSEMBLY', no: 9, name: 'Ghép', scope: 'PLAN', desc: 'Ghép các video thành tập.' },
];
// 261010 UI1a — khung mới = 8 bước (SCRIPT→VIDEO). ASSEMBLY chỉ còn ở WORKFLOW_STEPS (payload / route cũ `?step=ASSEMBLY`), KHÔNG vào thanh mới.
export const NEW_FLOW_STEPS: WorkflowStepMeta[] = WORKFLOW_STEPS.filter((s) => s.key !== 'ASSEMBLY');
export const NEW_FLOW_STEP_KEYS: WorkflowStepKey[] = NEW_FLOW_STEPS.map((s) => s.key);
// U32b (29/10) — thanh bước của BẢNG cấp tập = 7 ô: «Prompt» và «Video» gộp thành MỘT ô «Prompt & Video» (khoá đại diện = VIDEO). Trang chi tiết một video vẫn dùng NEW_FLOW_STEPS (8 khoá của API).
export const MERGED_STEP_KEY: WorkflowStepKey = 'VIDEO';
export const BOARD_STEPS: WorkflowStepMeta[] = NEW_FLOW_STEPS.filter((s) => s.key !== 'PROMPT').map((s) => (
  s.key === 'VIDEO' ? { ...s, no: 7, name: 'Prompt & Video', desc: 'Prompt gửi engine và các bản clip của từng video — sửa prompt rồi gen lại ngay tại đây.' } : s
));
export const BOARD_STEP_KEYS: WorkflowStepKey[] = BOARD_STEPS.map((s) => s.key);
/** `?step=PROMPT` và `?step=VIDEO` (liên kết cũ) đều mở ô gộp. */
export const isMergedStep = (k: WorkflowStepKey | null | undefined): boolean => k === 'PROMPT' || k === 'VIDEO';
export const boardStepKeyOf = (k: WorkflowStepKey): WorkflowStepKey => (k === 'PROMPT' ? MERGED_STEP_KEY : k);
/** 8 tóm tắt bước của thanh mới theo thứ tự cố định; payload thiếu bước ⇒ NOT_READY (không ném). */
export function newFlowStepsOf(steps: WorkflowStepSummary[]): WorkflowStepSummary[] {
  return NEW_FLOW_STEPS.map((m) => steps?.find((x) => x.key === m.key) ?? {
    key: m.key, scope: m.scope, state: 'NOT_READY' as const, total: 0, ready: 0, approved: 0, stale: 0, running: 0, failed: 0, review: 'NOT_AVAILABLE' as const,
  });
}
export const EVENT_STEP_KEYS: WorkflowEventStepKey[] = ['DIRECTING', 'CINEMATOGRAPHY', 'MAP', 'STORYBOARD', 'PROMPT', 'VIDEO'];

export const stepNameOf = (key: WorkflowStepKey): string => WORKFLOW_STEPS.find((s) => s.key === key)?.name ?? key;
export const isWorkflowStepKey = (v: unknown): v is WorkflowStepKey => WORKFLOW_STEPS.some((s) => s.key === v);

/**
 * Ba thứ KHÁC NHAU (discovery MỞ 1): có kết quả (`READY`) · người đã duyệt (`APPROVED`) · AI chấm (chưa có ở P2a).
 * Nhãn/tông phải phân biệt được READY với APPROVED — không gộp thành một chip xanh.
 */
export type StateTone = 'todo' | 'doing' | 'bad' | 'warn' | 'ready' | 'ok';
export const STATE_META: Record<WorkflowStepState, { label: string; tone: StateTone }> = {
  NOT_READY: { label: 'chưa tới', tone: 'todo' },
  RUNNING: { label: 'đang chạy', tone: 'doing' },
  ERROR: { label: 'lỗi', tone: 'bad' },
  STALE: { label: 'cũ', tone: 'warn' },
  READY: { label: 'có kết quả', tone: 'ready' },
  APPROVED: { label: 'đã duyệt', tone: 'ok' },
};

export const isDoneState = (s: WorkflowStepState): boolean => s === 'READY' || s === 'APPROVED';

/** Trạng thái của MỘT video ở MỘT bước. Bước cấp PLAN lấy theo plan (mọi video cùng giá trị). Payload thiếu bước ⇒ NOT_READY. */
export function stateOfEvent(ev: WorkflowEventStatus, key: WorkflowStepKey, planSteps: WorkflowStepSummary[]): WorkflowStepState {
  const meta = WORKFLOW_STEPS.find((s) => s.key === key);
  if (meta?.scope === 'EVENT') return ev.steps?.[key as WorkflowEventStepKey] ?? 'NOT_READY';
  return planSteps.find((s) => s.key === key)?.state ?? 'NOT_READY';
}

export interface EventDot { key: WorkflowStepKey; no: number; name: string; state: WorkflowStepState; tone: StateTone; label: string }
/** Dải 8 chấm của một video (UI1a: không còn Ghép) — nhãn đọc được cho trình đọc màn hình (không chỉ màu). */
export function eventDots(ev: WorkflowEventStatus, planSteps: WorkflowStepSummary[]): EventDot[] {
  return NEW_FLOW_STEPS.map((s) => {
    const state = stateOfEvent(ev, s.key, planSteps);
    return { key: s.key, no: s.no, name: s.name, state, tone: STATE_META[state].tone, label: `${s.name} — ${STATE_META[state].label}` };
  });
}

// U32b — gộp trạng thái Prompt + Video (ô 7). Ở đây (nhẹ, không kéo cây clip) vì workflowPrimaryPolicy dùng; promptVideoUtils re-export.
/** Trạng thái gộp của MỘT video: đã có clip ⇒ theo clip; chưa có ⇒ lỗi > cũ > đang chạy (ở Prompt HOẶC Video) > chưa có. */
export function mergedStateOf(prompt: WorkflowStepState | undefined, video: WorkflowStepState | undefined): WorkflowStepState {
  const p = prompt ?? 'NOT_READY';
  const v = video ?? 'NOT_READY';
  if (isDoneState(v) && p !== 'ERROR' && p !== 'RUNNING') return v;
  const any = (s: WorkflowStepState) => p === s || v === s;
  if (any('ERROR')) return 'ERROR';
  if (any('STALE')) return 'STALE';
  if (any('RUNNING')) return 'RUNNING';
  return isDoneState(v) ? v : 'NOT_READY';
}

/** Tóm tắt gộp cho ô 7: «xong» = video có clip (ready+approved của Video); lỗi / cũ / đang chạy cộng cả hai bước. Thiếu bước Video ⇒ undefined. */
export function mergedSummaryOf(steps: WorkflowStepSummary[] | undefined): WorkflowStepSummary | undefined {
  const v = (steps ?? []).find((x) => x.key === 'VIDEO');
  if (!v) return undefined;
  const p = (steps ?? []).find((x) => x.key === 'PROMPT');
  if (!p) return v;
  return { ...v, failed: v.failed + p.failed, stale: v.stale + p.stale, running: v.running + p.running };
}

/** Chữ đếm dưới tên bước: bước EVENT "x/y có kết quả" (gồm cả đã duyệt), bước PLAN theo trạng thái. */
export function stepCountText(step: WorkflowStepSummary): string {
  if (step.scope === 'PLAN') return STATE_META[step.state].label;
  return `${step.ready + step.approved}/${step.total} có kết quả`;
}

/** Màu viền thẻ video — chỉ xét 6 bước của riêng video (bước cấp plan đã có thanh 9 bước ở trên). lỗi/cũ > đang chạy > xong hết > còn dở. */
export type CardTone = 'bad' | 'doing' | 'done' | 'todo';
export function eventAggregateTone(ev: WorkflowEventStatus, _planSteps?: WorkflowStepSummary[]): CardTone {
  const states = EVENT_STEP_KEYS.map((k) => ev.steps?.[k] ?? 'NOT_READY');
  if (states.some((s) => s === 'ERROR' || s === 'STALE')) return 'bad';
  if (states.some((s) => s === 'RUNNING')) return 'doing';
  if (states.every(isDoneState)) return 'done';
  return 'todo';
}

// ── lọc ──
/** `ATTN` (cần xử lý = lỗi + thiếu + cũ) và `AUTO` (chưa chọn: tự chọn ATTN nếu có việc, không thì ALL) — UI1c; KHÔNG nằm trong `STATE_FILTERS`. */
export type StateFilter = 'ALL' | 'NOT_READY' | 'RUNNING' | 'ERROR' | 'STALE' | 'DONE' | 'ATTN' | 'AUTO';
export const STATE_FILTERS: Array<{ key: StateFilter; label: string }> = [
  { key: 'ALL', label: 'Tất cả' }, { key: 'ERROR', label: 'Lỗi' }, { key: 'STALE', label: 'Cũ' }, { key: 'RUNNING', label: 'Đang chạy' },
  { key: 'NOT_READY', label: 'Chưa tới' }, { key: 'DONE', label: 'Có kết quả' },
];
export interface BoardFilter { step: WorkflowStepKey | null; state: StateFilter }

const matches = (s: WorkflowStepState, f: StateFilter): boolean => f === 'ALL' || f === 'AUTO' || (f === 'ATTN' ? (s === 'ERROR' || s === 'STALE' || s === 'NOT_READY') : f === 'DONE' ? isDoneState(s) : s === f);

export function filterEvents(events: WorkflowEventStatus[], planSteps: WorkflowStepSummary[], f: BoardFilter): WorkflowEventStatus[] {
  if (f.state === 'ALL') return events;
  return events.filter((e) => (f.step
    ? matches(stateOfEvent(e, f.step, planSteps), f.state)
    : EVENT_STEP_KEYS.some((k) => matches(e.steps?.[k] ?? 'NOT_READY', f.state))));
}

export function stateFilterCounts(events: WorkflowEventStatus[], planSteps: WorkflowStepSummary[], step: WorkflowStepKey | null): Record<StateFilter, number> {
  const out = {} as Record<StateFilter, number>;
  for (const f of STATE_FILTERS) out[f.key] = filterEvents(events, planSteps, { step, state: f.key }).length;
  return out;
}

// ── nhóm theo cảnh ──
export interface SceneGroup { sceneId: string; sceneOrder: number; heading: string; events: WorkflowEventStatus[] }
/** Tên cảnh: `headings` (nếu có, vd. từ chi tiết plan) thắng; không có thì `sceneHeading` của event trong payload; không có nữa ⇒ "Cảnh N". */
export function groupByScene(events: WorkflowEventStatus[], headings: Record<string, string> = {}): SceneGroup[] {
  const sorted = [...events].sort((a, b) => a.sceneOrder - b.sceneOrder || a.eventOrder - b.eventOrder);
  const out: SceneGroup[] = [];
  for (const e of sorted) {
    let g = out.find((x) => x.sceneId === e.sceneId);
    if (!g) { g = { sceneId: e.sceneId, sceneOrder: e.sceneOrder, heading: headings[e.sceneId] || e.sceneHeading || `Cảnh ${e.sceneOrder}`, events: [] }; out.push(g); }
    g.events.push(e);
  }
  return out;
}

export function neighborEvents(events: WorkflowEventStatus[], eventId: string): { prev: WorkflowEventStatus | null; next: WorkflowEventStatus | null } {
  const i = events.findIndex((e) => e.eventId === eventId);
  if (i < 0) return { prev: null, next: null };
  return { prev: events[i - 1] ?? null, next: events[i + 1] ?? null };
}

/** Bao nhiêu video đã có clip (bước Video: có kết quả + đã duyệt). */
export function planProgress(steps: WorkflowStepSummary[]): { withClip: number; total: number } {
  const v = steps.find((s) => s.key === 'VIDEO');
  return v ? { withClip: v.ready + v.approved, total: v.total } : { withClip: 0, total: 0 };
}

// ── đường dẫn (deep-link) ──
export const workflowPath = (storyId: string, planId: string, q: { eventId?: string | null; step?: WorkflowStepKey | null } = {}): string => {
  const parts: string[] = [];
  if (q.eventId) parts.push(`event=${encodeURIComponent(q.eventId)}`);
  if (q.step) parts.push(`step=${q.step}`);
  return `/source-stories/${storyId}/film/${planId}/workflow${parts.length ? `?${parts.join('&')}` : ''}`;
};
/** Khung 4 bước là route riêng: khung Sky 9 bước giữ nguyên cho plan cũ. */
export const workflowFourPath = (storyId: string, planId: string): string => `/source-stories/${storyId}/film/${planId}/workflow-four`;
export function parseWorkflowQuery(search: string): { eventId: string | null; step: WorkflowStepKey | null } {
  const p = new URLSearchParams(search);
  const step = p.get('step');
  return { eventId: p.get('event') || null, step: isWorkflowStepKey(step) ? step : null };
}
/** UI cũ của plan (tab Film Plans). */
export const legacyPlanPath = (storyId: string, planId: string): string => `/source-stories/${storyId}?mode=film&tab=film&fplan=${encodeURIComponent(planId)}`;
/**
 * UI cũ mở thẳng MỘT video (`&fevent=<eventId>`): EventPlanTab cuộn tới thẻ video đó và nháy viền — đích của «Tách video» ở bước ④ (công cụ tách chỉ có ở UI cũ).
 * Không phải `?event=` của khung 8 bước.
 */
export const legacyEventPath = (storyId: string, planId: string, eventId: string): string => `${legacyPlanPath(storyId, planId)}&fevent=${encodeURIComponent(eventId)}`;

/** Danh sách plan của truyện (tab Film Plans) — KHÔNG có `?fplan=` ⇒ không vào UI cũ. Đích của nút «← Plans» ở khung mới. */
export const plansListPath = (storyId: string): string => `/source-stories/${storyId}?mode=film&tab=film`;
/**
 * Bấm thẻ plan trong danh sách: plan LUỒNG MỚI (`flowVersion === 'sky'`, BE trả cột này ở `GET stories/:id/plans`) ⇒ khung mới; còn lại (cũ / vắng / lạ) ⇒ UI cũ (`?fplan=`).
 * `?fplan=<id>` KHÔNG bao giờ tự chuyển hướng (đó là đường của nút «Mở UI cũ») ⇒ không vòng lặp.
 */
export function planOpenTargetOf(storyId: string, plan: { id: string; flowVersion?: string | null; workflowLayout?: string | null }): { kind: 'FOUR'; path: string } | { kind: 'LEGACY'; planId: string } {
  return plan.flowVersion === 'sky' && plan.workflowLayout === 'four' ? { kind: 'FOUR', path: workflowFourPath(storyId, plan.id) } : { kind: 'LEGACY', planId: plan.id };
}

// ── cảnh báo / chuông (tính tại chỗ, không lưu) ──
const SEV_RANK: Record<WorkflowAlertSeverity, number> = { ERROR: 0, WARN: 1, INFO: 2 };
export const sortAlerts = (alerts: WorkflowAlert[]): WorkflowAlert[] =>
  alerts.map((a, i) => ({ a, i })).sort((x, y) => SEV_RANK[x.a.severity] - SEV_RANK[y.a.severity] || x.i - y.i).map((x) => x.a);
export function bellSummary(alerts: WorkflowAlert[]): { total: number; error: number; warn: number; info: number; badge: number } {
  const n = (s: WorkflowAlertSeverity) => alerts.filter((a) => a.severity === s).length;
  const error = n('ERROR'), warn = n('WARN'), info = n('INFO');
  return { total: alerts.length, error, warn, info, badge: error + warn };
}
export const alertsForEvent = (alerts: WorkflowAlert[], eventId: string, includePlan = false): WorkflowAlert[] =>
  sortAlerts(alerts.filter((a) => a.subjectId === eventId || (includePlan && a.subjectType === 'PLAN')));
export function jobsForEvent(jobs: FilmRunningJob[], failures: FilmFailedJob[], eventId: string): { running: FilmRunningJob[]; failed: FilmFailedJob[] } {
  const mine = <T extends { eventId?: string }>(x: T) => !x.eventId || x.eventId === eventId;
  return { running: jobs.filter(mine), failed: failures.filter(mine) };
}

// ── lỗi tải ──
export type WorkflowErrorKind = 'NOT_SKY_PLAN' | 'NOT_FOUND' | 'NETWORK' | 'OTHER';
export function workflowErrorKind(err: any): WorkflowErrorKind {
  const status = err?.response?.status;
  if (status === 409 && err?.response?.data?.code === 'NOT_SKY_PLAN') return 'NOT_SKY_PLAN';
  if (status === 404) return 'NOT_FOUND';
  if (!err?.response && (err?.code === 'ERR_NETWORK' || /network/i.test(String(err?.message || '')))) return 'NETWORK';
  return 'OTHER';
}
export function workflowErrorText(err: any): string {
  switch (workflowErrorKind(err)) {
    case 'NOT_SKY_PLAN': return 'Plan này không dùng luồng mới — khung mới chỉ dùng cho plan dùng luồng mới. Hãy dùng giao diện cũ cho plan này.';
    case 'NOT_FOUND': return 'Không tìm thấy plan này (có thể đã bị xoá).';
    case 'NETWORK': return 'Không kết nối được máy chủ — kiểm tra kết nối rồi thử lại.';
    default: return 'Không tải được trạng thái plan — thử lại sau.';
  }
}

// ── hiển thị ──
export const truncateText = (s: string | null | undefined, max: number): string => {
  const t = String(s ?? '').replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};
/** Dòng meta của thẻ video: "00:12 · 3 shot · 2 thoại" (thiếu thời lượng ⇒ "—"). */
export const eventMetaText = (e: Pick<WorkflowEventStatus, 'genDurationS' | 'shotCount' | 'dialogueCount'>): string =>
  `${formatClock(e.genDurationS)} · ${e.shotCount ?? 0} shot · ${e.dialogueCount ?? 0} thoại`;
export const formatClock = (sec: number | null | undefined): string => {
  if (sec === null || sec === undefined || !Number.isFinite(sec) || sec < 0) return '—';
  const s = Math.floor(sec);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
